import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactGridLayout, useContainerWidth, verticalCompactor } from 'react-grid-layout';
// @ts-ignore - subpath exports not resolved with moduleResolution: node
import { GridBackground } from 'react-grid-layout/extras';
import { useDashboard } from '../contexts/DashboardContext';
import { WidgetCard } from '../WidgetCard/WidgetCard';
import { DashboardLayoutItem } from '../types';
import styles from './DashboardGrid.module.scss';

const COLS = 12;
const ROW_HEIGHT = 80;
const MARGIN: [number, number] = [16, 16];
const CONTAINER_PADDING: [number, number] = [0, 0];
const MIN_OVERLAY_ROWS = 6;

interface DashboardGridProps {
  onAddWidget: () => void;
}

export const DashboardGrid: React.FC<DashboardGridProps> = ({
  onAddWidget,
}) => {
  const { t } = useTranslation();
  const { displayedDashboard, isEditing, updateLayout } = useDashboard();
  const { width, containerRef, mounted } = useContainerWidth({ measureBeforeMount: true });

  const overlayRows = useMemo(() => {
    if (!displayedDashboard) return MIN_OVERLAY_ROWS;
    const layout = displayedDashboard.layout;
    if (layout.length === 0) return MIN_OVERLAY_ROWS;
    const bottomEdge = layout.reduce(
      (max, item) => Math.max(max, item.y + item.h),
      0,
    );
    return Math.max(MIN_OVERLAY_ROWS, bottomEdge + 2);
  }, [displayedDashboard]);

  const handleLayoutChange = useCallback(
    (layout: any) => {
      if (!isEditing) return;

      const items = Array.isArray(layout) ? layout : [];
      const newLayout: DashboardLayoutItem[] = items.map((item: any) => ({
        i: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minW: item.minW,
        minH: item.minH,
      }));

      updateLayout(newLayout);
    },
    [isEditing, updateLayout]
  );

  if (!displayedDashboard) return null;

  const { widgets, layout } = displayedDashboard;

  const minDimsByType: Record<string, { minW: number; minH: number }> = {
    metric:    { minW: 2, minH: 2 },
    card_grid: { minW: 4, minH: 3 },
    text:      { minW: 1, minH: 1 },
  };
  const defaultMins = { minW: 3, minH: 2 };

  const layoutWithMins = layout.map((item) => {
    if (item.minW != null && item.minH != null) return item;
    const chartType = widgets.find((w) => w.id === item.i)?.chartType;
    const mins = (chartType && minDimsByType[chartType]) ?? defaultMins;
    return {
      ...item,
      minW: item.minW ?? mins.minW,
      minH: item.minH ?? mins.minH,
    };
  });

  return (
    <div
      className={`${styles.gridContainer} ${isEditing ? styles.editing : ''}`}
      ref={containerRef}
    >
      {widgets.length === 0 ? (
        <div className={styles.emptyGrid}>
          <p>{t('No widgets yet.')}</p>
          {isEditing && (
            <button className={styles.addBtn} onClick={onAddWidget}>
              + {t('Add Widget')}
            </button>
          )}
        </div>
      ) : mounted && (
        <>
          {isEditing && (
            <GridBackground
              className={styles.gridOverlay}
              width={width}
              cols={COLS}
              rowHeight={ROW_HEIGHT}
              margin={MARGIN}
              containerPadding={CONTAINER_PADDING}
              rows={overlayRows}
              color="#f1f5f9"
              borderRadius={4}
            />
          )}
          <ReactGridLayout
            className={styles.grid}
            width={width}
            layout={layoutWithMins}
            gridConfig={{
              cols: COLS,
              rowHeight: ROW_HEIGHT,
              margin: MARGIN,
              containerPadding: CONTAINER_PADDING,
            }}
            dragConfig={{
              enabled: isEditing,
              handle: `.${styles.dragHandle}`,
            }}
            resizeConfig={{
              enabled: isEditing,
            }}
            onLayoutChange={handleLayoutChange}
            compactor={verticalCompactor}
          >
            {widgets.map((widget) => (
              <div key={widget.id}>
                <WidgetCard
                  widget={widget}
                  dragHandleClass={styles.dragHandle}
                />
              </div>
            ))}
          </ReactGridLayout>
        </>
      )}
    </div>
  );
};
